import { floatify } from 'lib/maths/mathUtils'
import { getAgentHubCommissionItemKey } from 'lib/agentHub/agentHubUtils'
import { isInstalmentFullyPaidOff } from './payInInstalmentsUtils'

export function getAgentHubTotals(order: App.Order, commission: number, isGrossPayment: boolean) {
  if (order.instalmentDetails) {
    const isPaid = isInstalmentFullyPaidOff(order.instalmentDetails)
    return { valueToSubtractFromNetTotal: !isPaid ? commission : 0, valueToAddToGrossTotal: isPaid ? commission : 0 }
  }
  if (order.reserveForZeroDetails) {
    const isPaid = !!order.reserveForZeroDetails.actualPaymentDate
    return { valueToSubtractFromNetTotal: !isPaid ? commission : 0, valueToAddToGrossTotal: isPaid ? commission : 0 }
  }
  if (order.depositDetails) {
    const isPaid = !!order.depositDetails.balance_paid_date
    return { valueToSubtractFromNetTotal: !isPaid ? commission : 0, valueToAddToGrossTotal: isPaid ? commission : 0 }
  }

  if (order.paymentScheduleDetails) {
    return { valueToSubtractFromNetTotal: commission, valueToAddToGrossTotal: 0 }
  }

  if (isGrossPayment) {
    return { valueToSubtractFromNetTotal: 0, valueToAddToGrossTotal: 0 }
  }

  return { valueToSubtractFromNetTotal: 0, valueToAddToGrossTotal: commission }
}

/**
 * Strips the leading transaction key from a commission key so lines that belong to the same
 * commission group (e.g. multiple rooms in a multi-room tour) resolve to a shared group id.
 */
export function getAgentHubCommissionGroupId(commissionKey: string, transactionKey?: string): string {
  return transactionKey && commissionKey.startsWith(`${transactionKey}-`)
    ? commissionKey.slice(transactionKey.length + 1)
    : commissionKey
}

/**
 * Allocates each commission group's total across its discount lines, returning the commission
 * discount amount keyed by the line's index in `itemDiscounts`.
 */
export function allocateAgentHubCommissionByLineIndex(
  itemDiscounts: Array<App.Checkout.ItemDiscount>,
  cartItems: Array<App.Checkout.AnyItem>,
  itemsCommission: App.AgentHubCommission['itemsCommission'],
): Map<number, number> {
  const commissionByLineIndex = new Map<number, number>()

  const commissionByGroupId = new Map<string, number>()
  cartItems.forEach((item) => {
    const commissionKey = getAgentHubCommissionItemKey(item)
    const commission = itemsCommission[commissionKey]?.itemDiscount
    if (commission !== undefined) {
      commissionByGroupId.set(getAgentHubCommissionGroupId(commissionKey, item.transactionKey), commission)
    }
  })

  if (!commissionByGroupId.size) {
    return commissionByLineIndex
  }

  const groupIdByItemId = new Map<string, string>()
  cartItems.forEach((item) => {
    const groupId = getAgentHubCommissionGroupId(getAgentHubCommissionItemKey(item), item.transactionKey)
    if (commissionByGroupId.has(groupId)) {
      groupIdByItemId.set(item.itemId, groupId)
    }
  })

  if (!groupIdByItemId.size) {
    return commissionByLineIndex
  }

  // Group the discount lines by commission group so each group's lines can share that group's
  // commission total proportionally to each line's discountable amount.
  const lineIndexesByGroupId = new Map<string, Array<number>>()
  itemDiscounts.forEach((itemDiscount, index) => {
    const groupId = itemDiscount.itemId ? groupIdByItemId.get(itemDiscount.itemId) : undefined
    if (!groupId) {
      return
    }
    const existing = lineIndexesByGroupId.get(groupId) ?? []
    existing.push(index)
    lineIndexesByGroupId.set(groupId, existing)
  })

  lineIndexesByGroupId.forEach((lineIndexes, groupId) => {
    const commissionTotal = commissionByGroupId.get(groupId)!
    const totalDiscountable = lineIndexes.reduce(
      (acc, index) => acc + (itemDiscounts[index].discountableTotal || 0),
      0,
    )
    let allocated = 0
    lineIndexes.forEach((lineIndex, position) => {
      const isLast = position === lineIndexes.length - 1
      if (isLast) {
        commissionByLineIndex.set(lineIndex, floatify(commissionTotal - allocated))
        return
      }
      const share = totalDiscountable > 0
        ? floatify(commissionTotal * ((itemDiscounts[lineIndex].discountableTotal || 0) / totalDiscountable))
        : floatify(commissionTotal / lineIndexes.length)
      commissionByLineIndex.set(lineIndex, share)
      allocated = floatify(allocated + share)
    })
  })

  return commissionByLineIndex
}
